import { Link } from 'react-router-dom'
import { Shield, AlertCircle, Scale, XCircle, CheckCircle } from 'lucide-react'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'

const brandFont = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", -apple-system, sans-serif',
  fontWeight: 100,
} as const

const bodyFont = {
  fontFamily: '"Helvetica Neue", sans-serif',
  fontWeight: 300,
} as const

function GlassCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border bg-white/[0.055] border-white/[0.09] backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/[0.14] transition-all duration-300 ${className}`}>
      {children}
    </div>
  )
}

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-consciousness-deep">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="mb-6 flex justify-center">
            <Scale className="w-16 h-16 text-trust-blue" strokeWidth={1} />
          </div>
          <h1
            className="text-5xl md:text-7xl tracking-[0.15em] mb-6 text-white"
            style={brandFont}
          >
            Terms of Service
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto mb-4" style={bodyFont}>
            The legal agreement governing your use of LUKHAS services
          </p>
          <p className="text-sm text-white/40" style={bodyFont}>
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </section>

      {/* Agreement Notice */}
      <section className="py-8 px-6">
        <div className="max-w-5xl mx-auto">
          <GlassCard>
            <div className="p-8">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-warning-amber flex-shrink-0 mt-1" />
                <div className="text-white/70" style={bodyFont}>
                  <p className="text-lg text-white mb-2" style={{ fontWeight: 400 }}>
                    Please Read Carefully
                  </p>
                  <p className="leading-relaxed">
                    By accessing or using LUKHAS services, you agree to be bound by these Terms. If you
                    disagree with any part, you may not access our services.
                  </p>
                </div>
              </div>
            </div>
          </GlassCard>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 px-6 pb-24">
        <div className="max-w-5xl mx-auto space-y-8">

          {/* Definitions */}
          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white" style={{ ...bodyFont, fontWeight: 300 }}>
                1. Definitions
              </h2>
              <div className="space-y-4 text-white/70" style={bodyFont}>
                <p className="leading-relaxed">
                  <strong className="text-white" style={{ fontWeight: 400 }}>&quot;Services&quot;</strong> refers to the LUKHAS consciousness
                  technology platform, including all websites, APIs, applications, and related services.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-white" style={{ fontWeight: 400 }}>&quot;You&quot;</strong> or <strong className="text-white" style={{ fontWeight: 400 }}>&quot;User&quot;</strong> refers
                  to the individual or entity accessing or using our Services.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-white" style={{ fontWeight: 400 }}>&quot;Content&quot;</strong> includes text, code, data, prompts, outputs,
                  and any other materials created, uploaded, or generated through our Services.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-white" style={{ fontWeight: 400 }}>&quot;LiD&quot;</strong> (Lambda ID) refers to our authentication and identity
                  management system.
                </p>
              </div>
            </div>
          </GlassCard>

          {/* Acceptable Use */}
          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white flex items-center gap-3" style={{ ...bodyFont, fontWeight: 300 }}>
                <CheckCircle className="w-6 h-6 text-trust-blue" />
                2. Acceptable Use Policy
              </h2>
              <div className="space-y-6 text-white/70" style={bodyFont}>
                <p className="leading-relaxed">You agree to use our Services responsibly. You may:</p>
                {[
                  'Use LUKHAS for personal, commercial, or research purposes',
                  'Create applications and products using our APIs',
                  'Share outputs generated by LUKHAS consciousness systems',
                  'Integrate LUKHAS into your workflows and applications',
                ].map((text) => (
                  <div key={text} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-verified-green flex-shrink-0 mt-1" />
                    <p>{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </GlassCard>

          {/* Prohibited Activities */}
          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white flex items-center gap-3" style={{ ...bodyFont, fontWeight: 300 }}>
                <XCircle className="w-6 h-6 text-error-red" />
                3. Prohibited Activities
              </h2>
              <div className="space-y-6 text-white/70" style={bodyFont}>
                <p className="leading-relaxed">You agree NOT to:</p>
                {[
                  'Use Services for illegal activities or to violate any laws',
                  'Generate or disseminate harmful, abusive, or discriminatory content',
                  'Attempt to reverse engineer, decompile, or extract models or algorithms',
                  'Bypass rate limits, authentication, or security measures',
                  'Use Services to create misleading AI-generated content without disclosure',
                  'Interfere with or disrupt the integrity or performance of Services',
                  'Use automated systems to scrape or harvest data without permission',
                  'Share your account credentials or allow unauthorized access',
                ].map((text) => (
                  <div key={text} className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-error-red flex-shrink-0 mt-1" />
                    <p>{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </GlassCard>

          {/* Sections 4-12 */}
          {[
            { title: '4. User Accounts and LiD Authentication', content: 'To access certain features, you must create an account via LiD. You are responsible for maintaining the confidentiality of your credentials, all activities under your account, and notifying us of unauthorized use.' },
            { title: '5. Intellectual Property Rights', content: 'All rights in the Services remain the exclusive property of LUKHAS AI. You retain ownership of content you create. Subject to compliance with these Terms, you own outputs generated by LUKHAS consciousness systems.' },
            { title: '6. Service Availability and Modifications', content: 'We strive for reliable Services but cannot guarantee uninterrupted access. We reserve the right to modify, suspend, or discontinue any aspect of Services with reasonable notice.' },
          ].map(({ title, content }) => (
            <GlassCard key={title}>
              <div className="p-8">
                <h2 className="text-2xl tracking-wide mb-6 text-white" style={{ ...bodyFont, fontWeight: 300 }}>
                  {title}
                </h2>
                <p className="text-white/70 leading-relaxed" style={bodyFont}>{content}</p>
              </div>
            </GlassCard>
          ))}

          {/* Disclaimer */}
          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white flex items-center gap-3" style={{ ...bodyFont, fontWeight: 300 }}>
                <AlertCircle className="w-6 h-6 text-warning-amber" />
                7. Disclaimer of Warranties
              </h2>
              <div className="space-y-4 text-white/70" style={bodyFont}>
                <p className="leading-relaxed uppercase text-white" style={{ fontWeight: 400 }}>
                  Services are provided &quot;as is&quot; and &quot;as available&quot; without warranties of any kind.
                </p>
                <p className="leading-relaxed">
                  We disclaim all implied warranties. You use Services at your own risk and are solely
                  responsible for evaluating outputs before relying on them.
                </p>
              </div>
            </div>
          </GlassCard>

          {/* Limitation of Liability */}
          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white flex items-center gap-3" style={{ ...bodyFont, fontWeight: 300 }}>
                <Shield className="w-6 h-6 text-error-red" />
                8. Limitation of Liability
              </h2>
              <div className="space-y-4 text-white/70" style={bodyFont}>
                <p className="leading-relaxed uppercase text-white" style={{ fontWeight: 400 }}>
                  LUKHAS AI shall not be liable for any indirect, incidental, special, consequential, or punitive damages.
                </p>
                <p className="leading-relaxed">
                  Our total liability shall not exceed the greater of $100 USD or the amount you paid us in
                  the 12 months preceding the claim.
                </p>
              </div>
            </div>
          </GlassCard>

          {[
            { title: '9. Indemnification', content: 'You agree to indemnify and hold harmless LUKHAS AI from any claims arising from your use or misuse of Services, violation of these Terms, or your content.' },
            { title: '10. Termination', content: 'We may suspend or terminate access immediately for any breach. You may terminate your account at any time. Upon termination, your right to use Services ceases.' },
            { title: '11. Governing Law', content: 'These Terms are governed by the laws of Delaware, USA. Disputes shall be resolved through binding arbitration. For EU users, mandatory consumer protection laws in your jurisdiction shall apply.' },
            { title: '12. General Provisions', content: 'These Terms constitute the entire agreement. If any provision is found unenforceable, the remaining provisions remain in full effect. We may modify these Terms at any time.' },
          ].map(({ title, content }) => (
            <GlassCard key={title}>
              <div className="p-8">
                <h2 className="text-2xl tracking-wide mb-6 text-white" style={{ ...bodyFont, fontWeight: 300 }}>
                  {title}
                </h2>
                <p className="text-white/70 leading-relaxed" style={bodyFont}>{content}</p>
              </div>
            </GlassCard>
          ))}

          {/* Contact */}
          <GlassCard>
            <div className="p-8 text-center">
              <h2 className="text-2xl tracking-wide mb-4 text-white" style={{ ...bodyFont, fontWeight: 300 }}>
                Questions About These Terms?
              </h2>
              <p className="text-white/50 mb-6" style={bodyFont}>
                Contact our legal team
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a
                  href="mailto:legal@lukhas.com"
                  className="px-6 py-2.5 rounded-full bg-trust-blue text-white text-sm tracking-[0.06em] hover:bg-trust-blue/90 transition-all inline-block"
                  style={bodyFont}
                >
                  legal@lukhas.com
                </a>
                <Link
                  to="/contact"
                  className="px-6 py-2.5 rounded-full border border-trust-blue/40 text-trust-blue text-sm tracking-[0.06em] hover:border-trust-blue/70 transition-all inline-block"
                  style={bodyFont}
                >
                  Contact Form
                </Link>
              </div>
            </div>
          </GlassCard>

        </div>
      </section>

      <SiteFooter
        columns={[
          {
            title: 'Legal',
            links: [
              { label: 'Privacy Policy', href: '/privacy' },
              { label: 'Terms of Service', href: '/terms' },
              { label: 'Cookie Policy', href: '/cookies' },
            ],
          },
          {
            title: 'Platform',
            links: [
              { label: 'Home', href: '/' },
              { label: 'Solutions', href: '/solutions' },
              { label: 'Contact', href: '/contact' },
            ],
          },
          {
            title: 'Ecosystem',
            links: [
              { label: 'Developers', href: 'https://lukhas.dev', external: true },
              { label: 'App Store', href: 'https://lukhas.store', external: true },
            ],
          },
        ]}
      />
    </div>
  )
}
